import { allowedValues, type FieldDef, type ListResult, multiValues, type ObjectName, OBJECTS, type RecordResult, type RecordTypeInfo, recordName } from '@crm/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type CSSProperties, useEffect, useState } from 'react';
import { api, qs } from '../lib/api';
import { useCampaignOptions, usePipelines, useProjects, useQueues, useSession, useStages, useUsers } from '../lib/session';

interface Props {
  field: FieldDef;
  value: unknown;
  onChange: (v: unknown) => void;
  error?: string;
  /** Display name for the current reference value. */
  refLabel?: string;
  /** The other values in the form (stage choices follow the chosen pipeline). */
  context?: Record<string, unknown>;
  /** The object being edited (record type choices). */
  objectName?: string;
  /** The record type chosen in the form: it can limit picklist values. */
  recordType?: RecordTypeInfo | null;
}

/** Picks a record of the referenced object by typing part of its name. */
function ReferenceInput({ field, value, onChange, refLabel, context }: Props) {
  // Deal Intelligence stakeholders can be created on the spot when they aren't in the CRM yet.
  const create = field.intel === 'stakeholders' && (field.references === 'contact' || field.references === 'account')
    ? (field.references === 'contact' && typeof context?.accountId === 'string' ? { accountId: context.accountId } : {})
    : undefined;
  return <RecordLookup id={`f-${field.name}`} object={field.references!} value={(value as string | null) ?? null} onChange={onChange} refLabel={refLabel} createDefaults={create} />;
}

/** Body for creating a contact or account from what was typed in a lookup. */
function quickCreateBody(object: ObjectName, text: string, defaults: Record<string, unknown>): Record<string, unknown> {
  if (object !== 'contact') return { ...defaults, name: text };
  const parts = text.trim().split(/\s+/);
  const lastName = parts.length > 1 ? parts.pop()! : parts[0]!;
  return { ...defaults, firstName: parts.length ? parts.join(' ') : undefined, lastName };
}

/** Type-ahead record picker, usable outside record forms (e.g. adding a related contact). */
export function RecordLookup({ id, object, value, onChange, refLabel, createDefaults }: {
  id: string; object: ObjectName; value: string | null; onChange: (id: string | null, name?: string) => void; refLabel?: string;
  /** Offer "+ New …" for what was typed, created with these values. */
  createDefaults?: Record<string, unknown>;
}) {
  const target = OBJECTS[object];
  const { permissions } = useSession();
  const [text, setText] = useState(refLabel ?? '');
  const [open, setOpen] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  useEffect(() => setText(refLabel ?? ''), [refLabel]);

  const results = useQuery({
    queryKey: ['lookup', target.name, text],
    enabled: open,
    queryFn: () => api<ListResult>(`/api/records/${target.name}${qs({ q: text || undefined, limit: 8 })}`),
  });
  const canCreate = !!createDefaults && !!text.trim() && permissions.objects[object].create
    && !results.data?.records.some((r) => recordName(target, r).toLowerCase() === text.trim().toLowerCase());
  const createNew = async () => {
    setCreateError(null);
    try {
      const r = await api<RecordResult>(`/api/records/${object}${qs({ allowDuplicates: 'true' })}`, { method: 'POST', body: quickCreateBody(object, text, createDefaults ?? {}) });
      const name = recordName(target, r.record);
      onChange(r.record.id, name);
      setText(name);
      setOpen(false);
    } catch (e) {
      setCreateError((e as Error).message);
    }
  };

  return (
    <div className="lookup">
      <input
        id={id}
        value={text}
        placeholder={`Search ${target.pluralLabel.toLowerCase()}…`}
        autoComplete="off"
        onChange={(e) => { setText(e.target.value); setOpen(true); if (!e.target.value) onChange(null); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && (
        <ul className="lookup-pop" role="listbox">
          {value != null && <li><button type="button" onMouseDown={() => { onChange(null); setText(''); }}>Clear</button></li>}
          {results.data?.records.map((r) => {
            const name = recordName(target, r);
            return (
              <li key={r.id}>
                <button type="button" onMouseDown={() => { onChange(r.id, name); setText(name); setOpen(false); }}>{name || '(no name)'}</button>
              </li>
            );
          })}
          {results.data?.records.length === 0 && <li className="muted">No matches</li>}
          {canCreate && (
            <li><button type="button" className="lookup-new" onMouseDown={(e) => { e.preventDefault(); void createNew(); }}>+ New {target.label.toLowerCase()} “{text.trim()}”</button></li>
          )}
        </ul>
      )}
      {createError && <span className="field-error">{createError}</span>}
    </div>
  );
}

/**
 * Several values from a list: click to pick or drop (stored as 'a;b' in list order). Open lists (e.g. brands)
 * also take a new name, which is added to the company's list for everyone.
 */
function MultiPick({ field: f, id, value, onChange, invalid, objectName }: {
  field: FieldDef; id: string; value: unknown; onChange: (v: unknown) => void; invalid: boolean; objectName?: string;
}) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Values just added stay visible until the refreshed list arrives.
  const [extra, setExtra] = useState<{ value: string; label: string }[]>([]);
  const options = [...(f.options ?? []), ...extra.filter((x) => !(f.options ?? []).some((o) => o.value === x.value))];
  const chosen = multiValues(value);
  const save = (next: string[]) => onChange(next.length ? options.filter((o) => next.includes(o.value)).map((o) => o.value).join(';') : null);
  const toggle = (v: string) => save(chosen.includes(v) ? chosen.filter((x) => x !== v) : [...chosen, v]);
  const add = async () => {
    const label = text.trim();
    if (!label || !objectName) return;
    const known = options.find((o) => o.label.toLowerCase() === label.toLowerCase());
    if (known) { if (!chosen.includes(known.value)) save([...chosen, known.value]); setText(''); return; }
    setBusy(true);
    setErr(null);
    try {
      const r = await api<{ value: string; label: string }>(`/api/lists/${objectName}/${f.name}/values`, { method: 'POST', body: { label } });
      setExtra((x) => [...x, r]);
      onChange([...chosen, r.value].join(';'));
      setText('');
      void qc.invalidateQueries({ queryKey: ['session'] });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="chips" role="group" id={id} aria-label={f.label} aria-invalid={invalid}>
      {options.filter((o) => !('inactive' in o && o.inactive) || chosen.includes(o.value)).map((o) => (
        <button type="button" key={o.value} className={`chip${chosen.includes(o.value) ? ' on' : ''}`} aria-pressed={chosen.includes(o.value)} onClick={() => toggle(o.value)}>
          {chosen.includes(o.value) && <span aria-hidden="true">✓ </span>}{o.label}
        </button>
      ))}
      {f.openList && objectName && (
        <span className="chip-add">
          <input value={text} maxLength={80} placeholder="+ Add…" aria-label={`Add a value to ${f.label}`} disabled={busy}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); void add(); } }} />
          {text.trim() && <button type="button" className="btn small" disabled={busy} onClick={() => void add()}>Add</button>}
        </span>
      )}
      {err && <span className="field-error">{err}</span>}
    </div>
  );
}

export function FieldInput(props: Props) {
  const { field: f, value, onChange, error } = props;
  const users = useUsers();
  const stages = useStages();
  const pipelines = usePipelines();
  const queues = useQueues();
  const campaigns = useCampaignOptions();
  const projects = useProjects();
  const { recordTypes } = useSession();
  const id = `f-${f.name}`;
  const common = { id, 'aria-invalid': !!error, 'aria-describedby': error ? `${id}-err` : undefined };
  const str = value == null ? '' : String(value);

  let input;
  switch (f.type) {
    case 'textarea':
      input = <textarea {...common} value={str} maxLength={f.maxLength} onChange={(e) => onChange(e.target.value)} rows={4} />;
      break;
    case 'percent': {
      // A slider for quick choices (steps of 5), with a small box for an exact value.
      const n = typeof value === 'number' ? value : value == null || value === '' ? null : Number(value);
      input = (
        <div className="percent-input">
          <input
            type="range" min={0} max={100} step={5} value={n ?? 0}
            aria-label={`${f.label} slider`}
            style={{ '--pct': `${n ?? 0}%` } as CSSProperties}
            className={n == null ? 'unset' : ''}
            onChange={(e) => onChange(Number(e.target.value))}
          />
          <span className="percent-box">
            <input {...common} type="number" inputMode="decimal" min={0} max={100} step="any" value={n ?? ''}
              onChange={(e) => onChange(e.target.value === '' ? null : Math.min(100, Math.max(0, Number(e.target.value))))} />
            <span aria-hidden="true">%</span>
          </span>
        </div>
      );
      break;
    }
    case 'number':
    case 'currency':
      input = (
        <input
          {...common}
          type="number"
          inputMode="decimal"
          step={f.type === 'number' ? 1 : 'any'}
          min={f.type === 'number' ? 0 : undefined}
          value={str}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
      );
      break;
    case 'date':
      input = <input {...common} type="date" value={str} onChange={(e) => onChange(e.target.value || null)} />;
      break;
    case 'boolean':
      // A Yes / No switch, so the choice reads clearly.
      input = (
        <label className="bool-toggle">
          <input {...common} type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} />
          <span className="bool-track" aria-hidden="true"><i /></span>
          <span className="bool-text">{value === true ? 'Yes' : 'No'}</span>
        </label>
      );
      break;
    case 'picklist':
      input = (
        <select {...common} value={str} onChange={(e) => onChange(e.target.value || null)}>
          <option value="">{f.required ? 'Choose…' : '—None—'}</option>
          {f.options?.filter((o) => o.value === value || (!o.inactive && allowedValues(f, props.recordType)?.some((a) => a.value === o.value)))
            .map((o) => <option key={o.value} value={o.value}>{o.label}{o.inactive ? ' (inactive)' : ''}</option>)}
        </select>
      );
      break;
    case 'multipicklist':
      input = <MultiPick field={f} id={id} value={value} onChange={onChange} invalid={!!error} objectName={props.objectName} />;
      break;
    case 'user':
      input = (
        <select {...common} value={str} onChange={(e) => onChange(e.target.value || null)}>
          {users.data?.filter((u) => u.isActive || u.id === value).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      );
      break;
    case 'stage':
      input = (
        <select {...common} value={str} onChange={(e) => onChange(e.target.value || null)}>
          {!value && <option value="">Default (first stage)</option>}
          {stages.data?.filter((s) => s.isActive && (!props.context?.pipelineId || s.pipelineId === props.context.pipelineId)).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      );
      break;
    case 'reference':
      input = <ReferenceInput {...props} />;
      break;
    case 'lookup': {
      const options = f.lookup === 'pipeline'
        ? (pipelines.data ?? []).filter((p) => p.isActive || p.id === value)
        : f.lookup === 'recordType' ? recordTypes.filter((r) => r.objectName === props.objectName && (r.isActive || r.id === value))
        : f.lookup === 'queue' ? queues.data ?? []
        : f.lookup === 'project' ? (projects.data ?? []).map((p) => ({ id: p.id, name: `${p.name} (${p.procurementStatus === 'on_hand' ? 'On-Hand' : 'Tender'})` }))
        : campaigns.data ?? [];
      input = (
        <select {...common} value={str} onChange={(e) => onChange(e.target.value || null)}>
          <option value="">{f.lookup === 'pipeline' ? 'Default pipeline' : f.lookup === 'recordType' ? '—No record type—' : '—None—'}</option>
          {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          {value != null && !options.some((o) => o.id === value) && <option value={str}>{props.refLabel ?? 'Current value'}</option>}
        </select>
      );
      break;
    }
    case 'email':
      input = <input {...common} type="email" value={str} maxLength={f.maxLength} onChange={(e) => onChange(e.target.value)} />;
      break;
    case 'phone':
      input = <input {...common} type="tel" value={str} maxLength={f.maxLength} onChange={(e) => onChange(e.target.value)} />;
      break;
    default:
      input = <input {...common} type="text" value={str} maxLength={f.maxLength} onChange={(e) => onChange(e.target.value)} />;
  }

  return (
    <div className={`field${f.type === 'textarea' || f.type === 'multipicklist' ? ' wide' : ''}`}>
      <label htmlFor={id}>{f.required && <span className="req" aria-hidden="true">* </span>}{f.label}</label>
      {input}
      {error && <span className="field-error" id={`${id}-err`}>{error}</span>}
    </div>
  );
}
